import _ from 'underscore';
import Promise from 'bluebird';
import { filterOutCampaignAndAdgroupName } from '../utils';

function lambdaConvertForLanguages(logic) {
  return (values) => {
    let url = '';

    _.each(values, (value, index) => {
      url += `contains(Language, '${value}')`;
      if (index !== values.length - 1) {
        url += ` ${logic} `;
      }
    });

    let finalurl = '';
    if (values.length > 0) {
      finalurl = `(${url})`;
      return finalurl;
    }
    return url;
  };
}

function getLanguagesItems(globalConfig, i18n) {
  const permissions = globalConfig.get('Permissions');

  if (permissions.IsDomesticSimplifiedChineseEnabled) {
    return _.all([{
      Id: 'ZH_HANS',
      Name: i18n.getString('Dimension_Lang_ZH_HANS'),
    }]);
  }

  let languageItems = [{
    Id: 'EN',
    Name: i18n.getString('Dimension_Lang_EN'),
  }, {
    Id: 'DA',
    Name: i18n.getString('Dimension_Lang_DA'),
  }, {
    Id: 'NL',
    Name: i18n.getString('Dimension_Lang_NL'),
  }, {
    Id: 'FI',
    Name: i18n.getString('Dimension_Lang_FI'),
  }, {
    Id: 'TH',
    Name: i18n.getString('Dimension_Lang_TH'),
  }, {
    Id: 'VI',
    Name: i18n.getString('Dimension_Lang_VI'),
  }, {
    Id: 'FR',
    Name: i18n.getString('Dimension_Lang_FR'),
  }, {
    Id: 'DE',
    Name: i18n.getString('Dimension_Lang_DE'),
  }, {
    Id: 'IT',
    Name: i18n.getString('Dimension_Lang_IT'),
  }, {
    Id: 'NB',
    Name: i18n.getString('Dimension_Lang_NO'),
  }, {
    Id: 'PT',
    Name: i18n.getString('Dimension_Lang_PT'),
  }, {
    Id: 'ES',
    Name: i18n.getString('Dimension_Lang_ES'),
  }, {
    Id: 'SV',
    Name: i18n.getString('Dimension_Lang_SV'),
  }, {
    Id: 'ZH-HANT',
    Name: i18n.getString('Dimension_Lang_ZH_HANT'),
  }];

  if (permissions.IsAdsGlobalizationPhase1Enabled) {
    languageItems = languageItems.concat([
      {
        Id: 'EL',
        Name: i18n.getString('Dimension_Lang_EL'),
      }, {
        Id: 'PL ',
        Name: i18n.getString('Dimension_Lang_PL'),
      }, {
        Id: 'BG',
        Name: i18n.getString('Dimension_Lang_BG'),
      }, {
        Id: 'HR',
        Name: i18n.getString('Dimension_Lang_HR'),
      }, {
        Id: 'CS',
        Name: i18n.getString('Dimension_Lang_CS'),
      }, {
        Id: 'ET',
        Name: i18n.getString('Dimension_Lang_ET'),
      }, {
        Id: 'HU',
        Name: i18n.getString('Dimension_Lang_HU'),
      }, {
        Id: 'LV',
        Name: i18n.getString('Dimension_Lang_LV'),
      }, {
        Id: 'LT',
        Name: i18n.getString('Dimension_Lang_LT'),
      }, {
        Id: 'MT',
        Name: i18n.getString('Dimension_Lang_MT'),
      }, {
        Id: 'RO',
        Name: i18n.getString('Dimension_Lang_RO'),
      }, {
        Id: 'SK',
        Name: i18n.getString('Dimension_Lang_SK'),
      }, {
        Id: 'SL',
        Name: i18n.getString('Dimension_Lang_SL'),
      },
    ]);
  }

  if (permissions.IsAdsGlobalizationPhase2Enabled) {
    languageItems = languageItems.concat([
      {
        Id: 'TR',
        Name: i18n.getString('Dimension_Lang_TR'),
      }, {
        Id: 'SR',
        Name: i18n.getString('Dimension_Lang_SR'),
      }, {
        Id: 'BS',
        Name: i18n.getString('Dimension_Lang_BS'),
      }, {
        Id: 'SQ',
        Name: i18n.getString('Dimension_Lang_SQ'),
      }, {
        Id: 'MK',
        Name: i18n.getString('Dimension_Lang_MK'),
      }, {
        Id: 'IS',
        Name: i18n.getString('Dimension_Lang_IS'),
      },
    ]);
  }

  if (permissions.IsAdsGlobalizationPhase6Enabled) {
    languageItems = languageItems.concat([
      {
        Id: 'AR',
        Name: i18n.getString('Dimension_Lang_AR'),
      },
      {
        Id: 'IW',
        Name: i18n.getString('Dimension_Lang_IW'),
      },
      {
        Id: 'RU',
        Name: i18n.getString('Dimension_Lang_RU'),
      },
    ]);
  }

  if (permissions.IsAdsGlobalizationPhase2JapanEnabled) {
    languageItems = languageItems.concat([
      {
        Id: 'JA',
        Name: i18n.getString('Dimension_Lang_JA'),
      },
    ]);
  }

  return _.sortBy(languageItems, language => language.Name);
}

class LanaguageDataSource {
  constructor(options) {
    this.globalConfig = options.globalConfig;
    this.i18n = options.i18n;
    this.languages = getLanguagesItems(this.globalConfig, this.i18n);
  }

  get(_viewFilter, searchFilter) {
    if (!searchFilter) {
      return Promise.resolve(this.languages);
    }

    const searchFilterLowerCase = searchFilter.toLowerCase();
    const languagesToReturn =
      _.select(
        this.languages,
        language => language.Name.toLowerCase().includes(searchFilterLowerCase)
      );

    return Promise.resolve(languagesToReturn);
  }

  getByIds() {
    return Promise.resolve(this.languages);
  }
}

function updateEntitySchemaForCampaignLanguages({
  entitySchema,
  i18n,
  globalConfig,
  scope,
}) {
  let entitySchemaCopy = entitySchema;

  entitySchemaCopy.properties.Language = {
    property: 'Languages',
    name: i18n.getString('ColumnName_Language'),
    title: i18n.getString('ColumnName_Language'),
    operatorDisplayNameMap: {
      containsany: i18n.getString('FilterOperator_ContainsAny'),
    },
    operators: ['containsany'],
    type: 'id',
    filterable: true,
    addtionalParams: {
      dataSource: new LanaguageDataSource({
        globalConfig,
        i18n,
      }),
      selectorOptions: {
        keyMap: {
          // eslint-disable-next-line camelcase
          Selector_Filter_Placeholder: () => i18n.getString('LanguageLabelText'),
        },
      },
    },
  };

  entitySchemaCopy = filterOutCampaignAndAdgroupName(scope, entitySchemaCopy);

  return entitySchemaCopy;
}

export function updateOdataOptionsForCampaignLanguages(options) {
  const optionsCopy = options || { custom: {} };

  _.extendOwn(optionsCopy.custom, {
    Language: {
      containsany: lambdaConvertForLanguages('or'),
    },
  });
  return optionsCopy;
}


export class DimensionsShoppingSchemaProcessor {
  constructor(options) {
    this.i18n = options.i18n;
    this.schemaI18nCampaign = options.schemaI18nCampaign;
    this.dataContextManager = options.dataContextManager;
    this.cacheService = options.cacheService;
    this.jsdata = options.jsdata;
    this.globalConfig = options.globalConfig;
    this.scope = options.scope;
  }

  updateEntitySchemaForFeatures(entitySchema) {
    const updateEntitySchema = {
      properties: {
      },
    };

    _.each(entitySchema.properties, (schema) => {
      updateEntitySchema.properties[schema.originalProperty] = schema;
    });

    updateEntitySchema.name = entitySchema.name;

    updateEntitySchemaForCampaignLanguages({
      entitySchema: updateEntitySchema,
      i18n: this.i18n,
      schemaI18nCampaign: this.schemaI18nCampaign,
      dataContextManager: this.dataContextManager,
      cacheService: this.cacheService,
      jsdata: this.jsdata,
      globalConfig: this.globalConfig,
      scope: this.scope,
    });
    return updateEntitySchema;
  }

  updateOdataOptions(odataOptions) {
    updateOdataOptionsForCampaignLanguages(odataOptions);
    return odataOptions;
  }
}
